<template>
  <page>
    <header-component
      :is-sticky="true"
      :logo="logoType"
      :content-props="{
        hasLoginLink: true,
        inviteCode,
        loginPath,
        signupPath,
        signupPopupEnabled: true,
        cta: joinButtonSpecs
      }"
    />

    <hero-section
      :login-path="loginPath"
      :signup-path="signupPath"
      :space-name="sanitizedSpaceName"
      :space-b2b="spaceB2b"
    />

    <avatar-friends :space-b2b="spaceB2b" />

    <spaces-category-explorer
      :space-b2b="spaceB2b"
      :login-path="loginPath"
      :signup-path="signupPath"
      :categories="categoryProps"
    />

    <spaces-explainer :space-b2b="spaceB2b" :login-path="loginPath" :signup-path="signupPath" />

    <people-spider :login-path="loginPath" :signup-path="signupPath" />

    <faq type="joinASpace" v-if="!spaceB2b" />

    <app-reviews v-if="!spaceB2b" :show-trial-end-date="false">
      <template v-slot:cta>
        <join-button class="z-10" :login-path="loginPath" :signup-path="signupPath" />
      </template>
    </app-reviews>
  </page>
</template>

<script>
import { getLastPathParam, safeAppendToUrl } from "@/utils/location"
import Page from "@/shared/page"
import HeroSection from "@/pages/spaces/join-page/components/hero-section"
import HeaderComponent from "@/features/header/components"
import AvatarFriends from "@/pages/spaces/join-page/components/avatar-friends"
import SpacesCategoryExplorer from "@/pages/spaces/join-page/components/category-explorer"
import PeopleSpider from "@/pages/spaces/join-page/components/people-spider"
import SpacesExplainer from "@/pages/spaces/join-page/components/spaces-explainer"
import Faq from "@/features/expendable-faq/expendable-faq"
import AppReviews from "@/features/app-reviews"
import JoinButton from "./join-button"
import globals from "@/utils/globals"
import { mapGetters } from "vuex"
import sanitizeHtml from "sanitize-html"

export default {
  name: "SpacesJoin",
  components: {
    HeroSection,
    Page,
    HeaderComponent,
    AvatarFriends,
    SpacesCategoryExplorer,
    PeopleSpider,
    SpacesExplainer,
    Faq,
    AppReviews,
    JoinButton
  },
  props: {
    categoryProps: {
      type: Array,
      default: () => [],
      required: true
    },
    spaceName: {
      type: String,
      required: true
    },
    spaceB2b: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      logoType: null
    }
  },
  computed: {
    ...mapGetters(["isUserLoggedIn"]),
    joinButtonSpecs() {
      return !this.isUserLoggedIn
        ? {
            copy: this.ctaCopy,
            openSignup: true,
            data: {
              "login-path": this.loginPath,
              "signup-path": this.signupPath
            }
          }
        : {
            copy: this.ctaCopy,
            openSignup: false,
            href: this.joinPathAfterLoginOrSignup
          }
    },
    inviteCode() {
      return getLastPathParam()
    },
    joinPathAfterLoginOrSignup() {
      return `${safeAppendToUrl("/accept")}`
    },
    signupPath() {
      return `${globals.routes.signup}?last_page_before_login=${encodeURIComponent(this.joinPathAfterLoginOrSignup)}`
    },
    loginPath() {
      return `${globals.routes.login}?last_page_before_login=${encodeURIComponent(this.joinPathAfterLoginOrSignup)}`
    },
    ctaCopy() {
      return this.$t("cta")
    },
    sanitizedSpaceName() {
      return sanitizeHtml(this.spaceName, { allowedTags: [], allowedAttributes: {} })
    },
    signupHeadline() {
      return this.$t("signupHeadline", { spaceName: this.sanitizedSpaceName })
    },
    loginHeadline() {
      return this.$t("loginHeadline", { spaceName: this.sanitizedSpaceName })
    }
  },
  mounted() {
    this.getLogoType()
    window.addEventListener("resize", this.getLogoType)
    this.initHeaderSignupTexts()
    return this.$store.dispatch("loadUsersAccounts")
  },
  methods: {
    getLogoType() {
      this.logoType = window && window.innerWidth <= 375 ? "no-text-2" : "full"
    },
    initHeaderSignupTexts() {
      const joinBtns = document.querySelectorAll(".js-open-signup-popup")
      if (!joinBtns) return
      joinBtns.forEach((button) => {
        button.addEventListener("click", () => {
          setTimeout(() => {
            const headlines = document.querySelectorAll(".auth-popup-form__headline")
            headlines.forEach((headline) => {
              headline.innerHTML = this.signupHeadline
            })
          }, 0)
        })
      })

      const loginBtns = document.querySelectorAll(".js-open-login-popup")
      if (!loginBtns) return
      loginBtns.forEach((button) => {
        button.addEventListener("click", () => {
          setTimeout(() => {
            const headlines = document.querySelectorAll(".auth-popup-form__headline")
            headlines.forEach((headline) => {
              headline.innerHTML = this.loginHeadline
            })
          }, 0)
        })
      })
    }
  },
  i18n: {
    messages: {
      en: {
        cta: "Join Space",
        signupHeadline:
          "Create your account to<br/>join the Space: {spaceName} <br /> <p class='mt-2 -mb-4 mx-auto' style='width:288px'>Start sharing your favorite aha-moments on Blinkist by creating your account</p>",
        loginHeadline: "Log in to join the Space: {spaceName}"
      },
      de: {
        cta: "Space beitreten",
        signupHeadline:
          "Erstelle einen Account, um dem Space beizutreten<br /> <p class='mt-2 -mb-4'>Registriere dich bei Blinkist, um Aha-Momente mit anderen zu teilen</p>",
        loginHeadline: "Melde dich an, um dem Space beizutreten"
      }
    }
  }
}
</script>

<style lang="scss">
.spaces.invites__accept {
  height: 100vh;
}
</style>
